export type CookieOptions = {
    domain?: string;
    maxAge?: number;
    path?: string;
    secure: boolean;
};

type RGIS = 'RGIS';
type USER_TOKEN = 'mut';
type CORAL_TOKEN = 'mct';
type VerifyIdTokenResult = {
    tokens: Record<RGIS | CORAL_TOKEN | USER_TOKEN, string | null | undefined>;
    emailHash?: string;
};
type EmailHash = {
    alg: string;
    digest: string;
};

type KnownUser = {
    userProfile: {
        email: string | null;
    };
};

export const ONE_YEAR_IN_SECONDS = 60 * 60 * 24 * 365;

export const initCookieOptions = (): CookieOptions => {
    // From https://github.com/themaven-net/web-chilliwack/blob/46ac5f2b9b2ab0570446bf46cb8208f7ab78f95f/src/util/UserLoginStore.ts#L32-L35
    // Potential issue: https://mavencorp.atlassian.net/browse/PA-418
    let domain = window.location.hostname;
    if (domain.split('.').length > 2) {
        domain = domain.substring(domain.indexOf('.'));
    }

    return {
        domain,
        path: '/',
        maxAge: ONE_YEAR_IN_SECONDS,
        secure: window.location.protocol !== 'http:',
    };
};

export const setCookie = (tokens: VerifyIdTokenResult['tokens'], options?: CookieOptions) => {
    for (const [key, value] of Object.entries(tokens)) {
        if (!value) {
            continue;
        }

        // https://github.com/themaven-net/web-chilliwack/blob/46ac5f2b9b2ab0570446bf46cb8208f7ab78f95f/src/util/UserLoginStore.ts#L38-L40
        // We don't want to encode the value for some reason, therefore this function should not be used
        // commonly.
        let cookie = `${key}=${value}`;

        if (options?.domain) {
            cookie += `; domain=${options.domain}`;
        }

        if (options?.maxAge !== undefined) {
            cookie += `; max-age=${options.maxAge}`;
        }

        if (options?.path) {
            cookie += `; path=${options.path}`;
        }

        if (options?.secure) {
            cookie += '; secure';
        }

        document.cookie = cookie;
    }
};

export const verifyIdToken = async (
    baseUrl: string,
    idToken: string,
    siteKeyword: string
): Promise<VerifyIdTokenResult> => {
    let res: Response;
    let data;
    try {
        res = await fetch(`${baseUrl}/api/public/identity/verify-id-token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ idToken, siteKeyword }),
        });
        data = await res.json();
    } catch (error) {
        throw error;
    }

    if (!res.ok) {
        throw new Error(`${data.message}/${res.status}`);
    }

    return {
        tokens: {
            // https://github.com/themaven-net/web-chilliwack/blob/46ac5f2b9b2ab0570446bf46cb8208f7ab78f95f/src/util/UserLoginStore.ts#L50
            RGIS: JSON.stringify(data?.tokens?.rgis || ''),
            mct: data?.tokens?.coralToken?.token,
            mut: data?.tokens?.userToken?.token,
        },
        emailHash: data?.userResponse?.emailHashes?.find((item: EmailHash) => item.alg === 'sha256')?.digest,
    };
};

export const sendEmailLink = async (
    baseUrl: string,
    email: string,
    siteId: string,
    targetUrl: string
): Promise<void> => {
    let res: Response;
    try {
        res = await fetch(`${baseUrl}/api/public/identity/send-email-link`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, siteId, targetUrl }),
        });
    } catch (error) {
        throw error;
    }

    if (!res.ok) {
        throw new Error(`${(await res.json()).message}/${res.status}`);
    }
};

// Regex from https://emailregex.com/
// See https://github.com/themaven-net/taiga/blob/master/server/src/util/__tests__/emailHelper.test.ts for use cases
const validEmailRegex =
    // eslint-disable-next-line max-len
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
export const isValidEmail = (email?: string): boolean => {
    if (!email) return false;
    return Boolean(email.match(validEmailRegex));
};

export function getKnownUser(baseUrl: string, rgisCookieValue?: string | null): Promise<KnownUser> {
    if (!rgisCookieValue || !baseUrl) {
        return Promise.resolve({
            userProfile: {
                email: null,
            },
        });
    }
    return fetch(`${baseUrl}/api/user/profile`, {
        headers: {
            'Content-Type': 'application/json',
            // For some reason the returned cookie has quotes.
            'X-RGIS': rgisCookieValue.replace(/^"/, '').replace(/"$/, ''),
        },
    }).then((resp) => {
        if (resp.status !== 200) {
            return {
                userProfile: {
                    email: null,
                },
            };
        }
        return resp.json();
    });
}
